import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import LavorisService from "../../../services/LavorisService";
import { RootState } from "../../../store/combineReducer";
import {
  setFranchiseBranding,
  setFranchiseError,
  setFranchiseLoading,
  setFranchiseSlug,
} from "../../../store/franchise/actions";
import {
  getBrandingSlug,
  getCurrentFranchiseSlug,
  getPersistStorageKey,
  getTokenStorageKey,
  resolveFranchiseSlugFromPath,
  setCurrentFranchiseSlug,
} from "../../../utils/franchise/franchiseSlug";

/**
 * Sincroniza slug desde la URL y carga branding público de la franquicia.
 */
export default function FranchiseBootstrap({
  children,
}: {
  children: React.ReactNode;
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const prevSlugRef = useRef<string | null>(getCurrentFranchiseSlug());
  const { slug: storedSlug, branding } = useSelector(
    (state: RootState) => state.franchise,
  );

  useEffect(() => {
    // URL completa del browser: React Router pathname no incluye basename `/socios/:slug`.
    const slugFromPath = resolveFranchiseSlugFromPath(
      typeof window !== "undefined" ? window.location.pathname : location.pathname,
    );
    const prevSlug = prevSlugRef.current;

    if (prevSlug !== slugFromPath) {
      window.localStorage.removeItem(`persist:${getPersistStorageKey(prevSlug)}`);
      window.localStorage.removeItem(getTokenStorageKey(prevSlug));
      prevSlugRef.current = slugFromPath;
    }

    setCurrentFranchiseSlug(slugFromPath);

    if (storedSlug !== slugFromPath) {
      dispatch(setFranchiseSlug(slugFromPath));
    }

    let cancelled = false;

    const loadBranding = async () => {
      const brandingSlug = getBrandingSlug(slugFromPath);
      const alreadyLoaded =
        branding &&
        (branding.slug === brandingSlug ||
          (branding.isRoot && brandingSlug === "lavoris" && !slugFromPath));

      if (alreadyLoaded && getCurrentFranchiseSlug() === slugFromPath) {
        return;
      }

      dispatch(setFranchiseLoading(true));
      try {
        const LavorisInstance = LavorisService.getInstance();
        const data = await LavorisInstance.Franchises.getPublicBySlug(
          brandingSlug,
        );
        if (!cancelled) {
          dispatch(setFranchiseBranding(data));
        }
      } catch (e: any) {
        console.error("Error loading franchise branding", e);
        if (!cancelled) {
          dispatch(
            setFranchiseError(
              e?.response?.data?.message ||
                e?.message ||
                "No se pudo cargar la franquicia",
            ),
          );
        }
      }
    };

    loadBranding();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location.pathname]);

  return <>{children}</>;
}
